import { loadStripe } from '@stripe/stripe-js';
import { axios, env, LocalStorageKey, localStorageUtil, PageLayout, PageNames } from '@diana/core';
import { useCallback, useState } from 'react';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { Box, Button, Card, useTheme } from '@mui/material';
import { billingQuery } from '@diana/core/build/queries/billing';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CHECKOUT_PAGE_NS } from './i18n';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';

const defaultEcosystemName = 'ecosystem';

export function Checkout() {
  const stripePromise = loadStripe(env('STRIPE_KEY'));
  const { t } = useTranslation(CHECKOUT_PAGE_NS);
  const { palette } = useTheme();
  const navigate = useNavigate();
  const [priceId] = useState(() =>
    localStorageUtil.getItem({ key: LocalStorageKey.billingPriceId }),
  );
  const redirectUrl =
    localStorageUtil.getItem({ key: LocalStorageKey.billingRedirectSuccess }) ??
    '/ecosystem/select';

  axios.setEnvironment(defaultEcosystemName);

  const fetchClientSecret = useCallback(async () => {
    const data = await billingQuery.createCheckoutSession().fn({ priceId });
    return data.clientSecret;
  }, [priceId]);

  const options = { fetchClientSecret };

  const containerSx = {
    overflowY: 'scroll',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
  };

  if (!priceId) {
    console.error('No price ID found in local storage, navigating user to the ecosystem select');
    localStorageUtil.removeItem({ key: LocalStorageKey.billingRedirectSuccess });
    return <Navigate to={redirectUrl} />;
  }

  return (
    <PageLayout disableSessionInfoLoader name={PageNames.CHECKOUT}>
      <Box
        display="flex"
        alignItems="center"
        flexDirection="column"
        justifyContent="flex-start"
        sx={containerSx}
      >
        <Button
          startIcon={<ArrowBackRoundedIcon sx={{ color: palette.text.secondary }} />}
          variant="text"
          onClick={() => navigate(redirectUrl)}
          sx={{
            alignSelf: 'flex-start',
            mt: '2px',
            ml: '26px',
            position: 'absolute',
            zIndex: 10000,
            color: 'inherit',
            '&:hover': {
              backgroundColor: palette.action.hover,
            },
          }}
        >
          {t('buttons.back')}
        </Button>

        <Card
          sx={{
            width: '100%',
            height: '100%',
            padding: 0,
            border: 0,
            borderRadius: 0,
            overflow: 'initial',
            paddingBottom: 2,
          }}
        >
          <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </Card>
      </Box>
    </PageLayout>
  );
}
